import { service } from '@ember/service';
import Component from '@glimmer/component';
import IdNameMismatchCard from 'garaje/components/approvals/screeners/id-name-mismatch-card';
import type ScreenerResult from 'garaje/models/screener-result';
import type { IdScanningNameMatch, IdScanningReport } from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type IdNameReview from 'garaje/utils/id-name-review';

interface IdScanningSignature {
  Args: {
    result: ScreenerResult<IdScanningReport>;
    model: GuestWithVisitApproval;
    nameReview: IdNameReview;
  };
  Blocks: {
    default: [];
  };
}

export default class IdScanning extends Component<IdScanningSignature> {
  @service declare featureFlags: FeatureFlagsService;

  get report(): IdScanningReport {
    return this.args.result.report;
  }

  get nameMatch(): IdScanningNameMatch | undefined {
    return this.report?.['name-match'];
  }

  /**
   * Whether to render the editable name-mismatch card. Gated on the
   * `id-name-mismatch-screener` split and the report flagging a name mismatch.
   */
  get isNameMismatch(): boolean {
    return this.featureFlags.isEnabled('id-name-mismatch-screener') && Boolean(this.nameMatch?.mismatch);
  }

  /** The name the visitor registered (typed) with. */
  get registeredName(): string {
    return this.args.model.fullName;
  }

  <template>
    {{#if @model.visitApproval.idScanningPendingResult}}
      <li class="text-sm mt-4" data-test-id-scanning-pending>
        <img src="/assets/images/progress-small-gray.svg" alt="" role="presentation" />&nbsp;
        <span class="whitespace-nowrap">
          Visitor’s ID is processing.
        </span>
      </li>
    {{else if this.isNameMismatch}}
      <IdNameMismatchCard
        @nameMatch={{this.nameMatch}}
        @registeredName={{this.registeredName}}
        @nameReview={{@nameReview}}
      />
    {{else if @model.visitApproval.idScanningReviewResult}}
      <li class="text-sm mt-4">{{@result.idScanningFailureMessage}}</li>
    {{/if}}
    {{yield}}
  </template>
}
